import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';

import {
    container,
    settings,
    title,
    range,
    accountSwitcher,
    paymentSwitcher,
    durationLabels,
    durationLabel,
    durationLabelText,
    hint,
    arrow,
    durationArrow,
    desktop,
    mobile,
    switcherButton,
    cardsBox,
    cardsLabel,
} from './pricing.module.scss';
import { mainGrid } from '../../style/grid.module.scss';
import PricingArrow from '../../assets/images/svg/pricing-arrow.svg';
import { IProduct } from '../../models/product.model';
import { IProductAttributeValue } from '../../models/product-attribute.model';
import { getProductVariants } from '../../utills/get-product-variants';
import { getLessImportantHeading } from '../../utills/get-less-important-heading';
import { getSubscriptionDurationLabel } from '../../utills/get-subscription-duration-label';
import listItemToJSONLD from '../../utills/list-item-to-json-ld';
import processStructuredData from '../../utills/process-structured-data';

import Title from '../atoms/title';
import PillSwitcher, { IPillSwitcherProps } from '../molecules/pill-switcher';
import StepRange from '../atoms/step-range';
import PricingCards from '../molecules/pricing-cards';

interface IPricingProps {
    className?: string;
    TitleTag?: React.ElementType;
    useStructuredData?: boolean;
    hideTitle?: boolean;
}

const DURATION_TYPE_ATTRIBUTE_ID = 1;
const ACCOUNT_TYPE_ATTRIBUTE_ID = 3;
const PAYMENT_TYPE_ATTRIBUTE_ID = 5;
const INDIVIDUAL_ACCOUNT_ATTRIBUTE_VALUE = Number(process.env.INDIVIDUAL_ACCOUNT_ATTRIBUTE);
const BASE_STANDARD_INDIVIDUAL_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_STANDARD_INDIVIDUAL_PRICE_GROSS_INTEGER
);
const BASE_PREMIUM_INDIVIDUAL_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_PREMIUM_INDIVIDUAL_PRICE_GROSS_INTEGER
);
const BASE_PREMIUM_PLUS_INDIVIDUAL_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_PREMIUM_PLUS_INDIVIDUAL_PRICE_GROSS_INTEGER
);
const BASE_STANDARD_COUPLE_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_STANDARD_COUPLE_PRICE_GROSS_INTEGER
);
const BASE_PREMIUM_COUPLE_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_PREMIUM_COUPLE_PRICE_GROSS_INTEGER
);
const BASE_PREMIUM_PLUS_COUPLE_PRICE_GROSS_INTEGER = Number(
    process.env.BASE_PREMIUM_PLUS_COUPLE_PRICE_GROSS_INTEGER
);

const standardAttribute = { value: Number(process.env.STANDARD_ATTRIBUTE) };
const premiumAttribute = { value: Number(process.env.PREMIUM_ATTRIBUTE) };
const premiumPlusAttribute = { value: Number(process.env.PREMIUM_PLUS_ATTRIBUTE) };
const oneTimePaymentAttribute = { value: Number(process.env.ONE_TIME_PAYMENT_ATTRIBUTE) };

interface IPricingQueryResult {
    product: Pick<IProduct, 'productId' | 'variants' | 'attributesDefinitions' | 'slug'>;
    site: { siteMetadata: { url: string } };
}

const Pricing: React.FC<IPricingProps> = ({
    className = '',
    TitleTag = 'h2',
    useStructuredData,
    hideTitle = false,
}) => {
    const { product, site } = useStaticQuery<IPricingQueryResult>(query);
    const durationTypes = [...getDurationOptions(product)].sort(
        (a, b) => parseInt(a.label) - parseInt(b.label)
    );
    const accountTypes = getAttributeOptions(product, ACCOUNT_TYPE_ATTRIBUTE_ID);
    const paymentTypes = getAttributeOptions(product, PAYMENT_TYPE_ATTRIBUTE_ID);

    const [durationType, setDurationType] = useState(durationTypes[durationTypes.length - 1]);
    const [accountType, setAccountType] = useState(accountTypes[0]);
    const [paymentType, setPaymentType] = useState(paymentTypes[0]);
    const isOneTimePayment = paymentType.value === oneTimePaymentAttribute.value;

    const standardVariant = getProductVariants(product.variants, [
        durationType,
        accountType,
        paymentType,
        standardAttribute,
    ])[0];
    const premiumVariant = getProductVariants(product.variants, [
        durationType,
        accountType,
        paymentType,
        premiumAttribute,
    ])[0];
    const premiumPlusVariant = getProductVariants(product.variants, [
        durationType,
        accountType,
        paymentType,
        premiumPlusAttribute,
    ])[0];

    const isIndividual = accountType.value === INDIVIDUAL_ACCOUNT_ATTRIBUTE_VALUE;
    const baseStandardPrice = isIndividual
        ? BASE_STANDARD_INDIVIDUAL_PRICE_GROSS_INTEGER
        : BASE_STANDARD_COUPLE_PRICE_GROSS_INTEGER;
    const basePremiumPrice = isIndividual
        ? BASE_PREMIUM_INDIVIDUAL_PRICE_GROSS_INTEGER
        : BASE_PREMIUM_COUPLE_PRICE_GROSS_INTEGER;
    const basePremiumPlusPrice = isIndividual
        ? BASE_PREMIUM_PLUS_INDIVIDUAL_PRICE_GROSS_INTEGER
        : BASE_PREMIUM_PLUS_COUPLE_PRICE_GROSS_INTEGER;

    const seoProducts = [
        {
            name: `Standard`,
            slug: `/sklep/${product.slug}/?variant=${standardVariant.variantId}`,
        },
        {
            name: `Premium`,
            slug: `/sklep/${product.slug}/?variant=${premiumVariant.variantId}`,
        },
        {
            name: `Premium+`,
            slug: `/sklep/${product.slug}/?variant=${premiumPlusVariant.variantId}`,
        },
    ];

    const handleDurationTypeChange: IPillSwitcherProps['onChange'] = (option) => {
        setDurationType(option as IProductAttributeValue & { installmentCount: number });
    };

    const handleAccountTypeChange: IPillSwitcherProps['onChange'] = (option) => {
        setAccountType(option as IProductAttributeValue);
    };

    const handlePaymentTypeChange: IPillSwitcherProps['onChange'] = (option) => {
        setPaymentType(option as IProductAttributeValue);
    };

    return (
        <div className={`${className} ${container} ${mainGrid}`}>
            {useStructuredData && (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            ...processStructuredData({
                                '@type': 'ItemList',
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                itemListElement: listItemToJSONLD(
                                    seoProducts,
                                    site.siteMetadata.url
                                ),
                            }),
                        })}
                    </script>
                </Helmet>
            )}
            <div className={settings}>
                {!hideTitle && (
                    <Title className={title} Tag={TitleTag}>
                        {copy.title}
                    </Title>
                )}
                {accountTypes.length > 0 && (
                    <PillSwitcher
                        className={accountSwitcher}
                        title={copy.accountLabel}
                        options={accountTypes}
                        activeOption={accountType}
                        onChange={handleAccountTypeChange}
                    />
                )}
                {paymentTypes.length > 0 && (
                    <div className={paymentSwitcher}>
                        <PillSwitcher
                            title={copy.paymentLabel}
                            options={paymentTypes}
                            activeOption={paymentType}
                            onChange={handlePaymentTypeChange}
                            buttonClassName={switcherButton}
                        />
                    </div>
                )}
                {durationTypes.length > 0 && (
                    <StepRange
                        className={range}
                        label={copy.durationLabel}
                        options={durationTypes}
                        activeOption={durationType}
                        onChange={handleDurationTypeChange}
                    >
                        <div className={durationLabels}>
                            {durationTypes.map((duration) => {
                                return (
                                    <div className={durationLabel} key={`label-${duration.value}`}>
                                        {durationTypesHints[duration.value.toString()] && (
                                            <div className={hint}>
                                                <p className={mobile}>
                                                    {
                                                        durationTypesHints[
                                                            duration.value.toString()
                                                        ].mobile
                                                    }
                                                </p>
                                                <p className={desktop}>
                                                    {
                                                        durationTypesHints[
                                                            duration.value.toString()
                                                        ].desktop
                                                    }
                                                </p>
                                                <PricingArrow
                                                    className={`${arrow} ${durationArrow}`}
                                                />
                                            </div>
                                        )}
                                        <p className={durationLabelText}>{duration.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </StepRange>
                )}
            </div>
            <div className={cardsBox}>
                <p className={cardsLabel}>{copy.cardsLabel}</p>
                <PricingCards
                    TitleTag={getLessImportantHeading(TitleTag)}
                    productId={product.productId}
                    isOneTimePayment={isOneTimePayment}
                    standard={{
                        variantId: standardVariant.variantId,
                        price: getPrice(
                            standardVariant.price.grossInteger,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                        maxPrice: getMaxPrice(
                            baseStandardPrice,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                    }}
                    premium={{
                        variantId: premiumVariant.variantId,
                        price: getPrice(
                            premiumVariant.price.grossInteger,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                        maxPrice: getMaxPrice(
                            basePremiumPrice,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                    }}
                    premiumPlus={{
                        variantId: premiumPlusVariant.variantId,
                        price: getPrice(
                            premiumPlusVariant.price.grossInteger,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                        maxPrice: getMaxPrice(
                            basePremiumPlusPrice,
                            durationType.installmentCount,
                            isOneTimePayment
                        ),
                    }}
                />
            </div>
        </div>
    );
};

function getPrice(grossInteger: number, installmentCount: number, isOneTimePayment: boolean) {
    if (isOneTimePayment) return grossInteger / 100;
    return grossInteger / installmentCount / 100;
}

function getMaxPrice(grossInteger: number, installmentCount: number, isOneTimePayment: boolean) {
    if (isOneTimePayment) return (grossInteger * installmentCount) / 100;
    return grossInteger / 100;
}

function getAttributeOptions(
    product: Pick<IProduct, 'productId' | 'variants' | 'attributesDefinitions' | 'slug'>,
    attributeId: number
) {
    return (
        product.attributesDefinitions?.find((attribute) => {
            return attribute.attributeId === attributeId;
        })?.values || []
    );
}

function getDurationOptions(
    product: Pick<IProduct, 'productId' | 'variants' | 'attributesDefinitions' | 'slug'>
) {
    const options = getAttributeOptions(product, DURATION_TYPE_ATTRIBUTE_ID);
    return options.map((option) => {
        return {
            ...option,
            label: getSubscriptionDurationLabel(option.label),
            installmentCount: Number(option.label),
        };
    });
}

const durationTypesHints: Record<string, { mobile: string; desktop: string }> = {
    '1': {
        mobile: 'do -50%',
        desktop: 'Oszczędź 50%',
    },
    '2': {
        mobile: 'do -40%',
        desktop: 'Oszczędź 40%',
    },
    '3': {
        mobile: 'do -30%',
        desktop: 'Oszczędź 30%',
    },
};

const copy = {
    title: 'Holistycznie zadbamy o Ciebie i Twoje efekty',
    accountLabel: '1. Wybierz typ konta',
    paymentLabel: '2. Wybierz model płatności',
    durationLabel: '3. Wybierz przez jaki czas mamy Ci służyć naszą pomocą',
    cardsLabel: '4. Wybierz pakiet',
};

const query = graphql`
    query {
        product(productId: { eq: 1 }) {
            productId
            slug
            attributesDefinitions {
                ...attributesDefinitionsFields
            }
            variants {
                variantId
                attributesValues {
                    attributeId
                    value
                    label
                }
                price {
                    grossInteger
                }
            }
        }
        site {
            ...siteFields
        }
    }
`;

export default Pricing;
